import FileSaver from "file-saver";
import { CalendarEvent, ics } from "calendar-link";
import VerifiedSenders from "./verified-senders";
import GdprPolicy from "./gdpr-policy";
import Field from "./field";
import Documents from "./documents";
import Auth from "./auth";
import { timeout } from "../utils";
import IUserData from "../interface/user-data";
import IField from "../interface/field";
import IDocument from "../interface/document";
import IApiTokenVerification from "../interface/api-token-verification";
import IApiTokenQuestion from "../interface/api-token-question";
import IApiTokenGdpr from "../interface/api-token-gdpr";
import IApiTokenDataVerification from "../interface/api-token-data-verification";
import IApiTokenDataQuestion from "../interface/api-token-data-question";
import IApiTokenDataGdpr from "../interface/api-token-data-gdpr";
import ServiceName from "../enum/service-name";
import LogicalValue from "../enum/logical-value";
import ErrorCode from "../enum/error-code";

class PublicApiCommonSingleton {

    /* Public Methods */

    public async processGdprResponse(token: string): Promise<void> {
        const data: IApiTokenDataGdpr = this.encodeTokenGdpr(token as string);
        await this.login(data.appId, data.instance);
        await GdprPolicy.set(data.email, data.value);
    }

    public async processQuestionResponse(token: string): Promise<void> {
        const data: IApiTokenDataQuestion = this.encodeTokenQuestion(token as string);
        await this.login(data.appId, data.instance);
        const field: IField | null = Field.get(data.fieldId);
        if (field) {
            const doc: IDocument | null = await Documents.loadOne(field.entityId, data.recordId);
            if (doc) {
                doc[`c${data.fieldId}`] = data.value;
                await Documents.replace(field.entityId, doc);
            } else {
                throw new Error(ErrorCode.NoDocument);
            }
        } else {
            throw new Error(ErrorCode.NoField);
        }
    }

    public async processSenderVerification(token: string): Promise<void> {
        const data: IApiTokenDataVerification = this.encodeTokenVerification(token as string);
        await this.login(data.appId, data.instance);
        await VerifiedSenders.setVerified(data.address, data.service);
    }

    public async processIcs(token: string): Promise<void> {
        try {
            const event: CalendarEvent = JSON.parse(atob(decodeURIComponent(token)));
            const base64: string = ics(event);
            FileSaver.saveAs(base64, `${event.title}.ics`);
            await timeout(2000);
        } catch (e) {
            throw new Error(ErrorCode.InvalidToken);
        }
    }

    /* Private Public Methods */

    public async login(appId: string, instance: string): Promise<IUserData> {
        const defaultInstance: string = 'main';
        const username: string = `${appId}_public_user`;
        const password: string = btoa(username);
        try {
            return await Auth.login(username, password, instance || defaultInstance, appId);
        } catch (e) {
            throw Error(ErrorCode.AuthFailed);
        }
    }

    /* Private Methods */

    private encodeTokenGdpr(token: string): IApiTokenDataGdpr {
        try {
            const data: IApiTokenGdpr = JSON.parse(atob(token));
            const result: IApiTokenDataGdpr = {
                email: atob(data.e),
                appId: atob(data.a),
                value: data.v,
                instance: atob(data.i)
            };
            return result;
        } catch (e) {
            throw Error(ErrorCode.InvalidToken);
        }
    }

    private encodeTokenQuestion(token: string): IApiTokenDataQuestion {
        try {
            const data: IApiTokenQuestion = JSON.parse(atob(token));
            const result: IApiTokenDataQuestion = {
                appId: atob(data.a),
                instance: atob(data.i),
                recordId: Number(atob(data.r)),
                fieldId: Number(atob(data.f)),
                value: atob(data.v) as LogicalValue
            };
            return result;
        } catch (e) {
            throw Error(ErrorCode.InvalidToken);
        }
    }

    private encodeTokenVerification(token: string): IApiTokenDataVerification {
        try {
            const data: IApiTokenVerification = JSON.parse(atob(token));
            const result: IApiTokenDataVerification = {
                appId: atob(data.a),
                instance: atob(data.i),
                address: atob(data.e),
                service: atob(data.s) as ServiceName
            };
            return result;
        } catch (e) {
            throw Error(ErrorCode.InvalidToken);
        }
    }

}

const PublicApiCommon = new PublicApiCommonSingleton();

export default PublicApiCommon;